/** @jsxImportSource theme-ui */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import { AppStateContext } from 'api/AppStateContext';
import DiseaseActivityApi from 'api/DiseaseActivityApi';
import { showNotification } from 'app-redux/notification.saga';
import * as dto from 'client/dto';
import constants from 'ga/constants';
import { useInsightsRouting } from 'hooks/useInsightsRouting';
import { useNonMobileEffect } from 'hooks/useNonMobileEffect';
import { RiskDirectionType } from 'models/RiskCategories';
import { notifyEvent } from 'utils/analytics';
import { DisableTRANSPAR, SpeciesIdHuman } from 'utils/constants';
import { parseIntOrNull } from 'utils/stringHelpers';

import aoiLayer from 'map/aoiLayer';
import mapEventDetailView from 'map/eventDetails';
import mapEventsView from 'map/eventsLayer';

import { GetSelectedRisk } from 'components/RisksProjectionCard/RisksProjectionCard';
import { ActivePanel } from 'components/SidebarView/sidebar-types';

import { DiseaseActivityHistoryPanel } from '../CommonPanels/DiseaseActivityHistoryPanel';
import { EventDetailPanel } from '../EventDetailPanel';
import { TransparencyPanel } from '../TransparencyPanel';
import { EventListPanel } from './EventListPanel';

const EventView: React.FC = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { navigateTo } = useInsightsRouting();
  const { appState, amendState } = useContext(AppStateContext);

  const [isMinimizedEventListPanel, setIsMinimizedEventListPanel] = useState(false);
  const [isMinimizedEventDetailPanel, setIsMinimizedEventDetailPanel] = useState(false);
  const [isMinimizedParametersPanel, setIsMinimizedParametersPanel] = useState(false);
  const [isMinimizedHistoryPanel, setIsMinimizedHistoryPanel] = useState(false);

  const [events, setEvents] = useState<dto.GetEventListModel>(null);
  const [isEventListLoading, setIsEventListLoading] = useState(false);
  const [selectedEventDetails, setSelectedEventDetails] = useState<dto.GetEventModel>(null);
  const [selectedRiskType, setSelectedRiskType] = useState<RiskDirectionType>(null);

  const [diseaseId, setDiseaseId] = useState<number>(null);
  const [speciesId, setSpeciesId] = useState(SpeciesIdHuman);
  const [countryId, setCountryId] = useState<number>(null);
  const [activityDate, setActivityDate] = useState(null);
  const [hasActivity, setHasActivity] = useState(false);
  const [hasParameters, setHasParameters] = useState(false);

  useEffect(() => {
    const parsedDiseaseId = pathname.match(/all\/disease\/(\d+)/)?.[1];
    setDiseaseId(parseIntOrNull(parsedDiseaseId));

    const parsedSpeciesId = pathname.match(/\/species\/(\d+)/)?.[1];
    setSpeciesId(parseIntOrNull(parsedSpeciesId) || SpeciesIdHuman);

    const parsedCountryId = pathname.match(/\/country\/(\d+)/)?.[1];
    setCountryId(parseIntOrNull(parsedCountryId));

    const parsedActivityDate = pathname.match(/\/activity\/(\d{4}-\d{2}-\d{2})/)?.[1];
    setActivityDate(parsedActivityDate);

    setHasActivity(!!parsedActivityDate?.length || pathname.includes('/activity'));
    setHasParameters(pathname.includes('/parameters'));
  }, [pathname]);

  const disactIds = useMemo(
    () => ({
      diseaseId: diseaseId,
      speciesId: speciesId,
      countryId: countryId,
    }),
    [countryId, diseaseId, speciesId]
  );

  const activePanel = useMemo<ActivePanel>(
    () =>
      countryId && hasParameters
        ? 'ParametersPanel'
        : countryId && hasActivity
        ? 'HistoryPanel'
        : countryId
        ? 'EventDetailPanel'
        : 'EventListPanel',
    [countryId, hasParameters, hasActivity]
  );
  const isVisibleEDP = useMemo(() => !!countryId, [countryId]);
  const isVisibleTRANSPAR = useMemo(() => !!countryId && hasParameters, [countryId, hasParameters]);

  // TODO: 2b9c6c41: dup
  function kickOutWithNotification(url: string, message: string) {
    dispatch(
      showNotification({
        type: 'SHOW_NEUTRAL_NOTIFICATION',
        payload: message,
      })
    );
    navigateTo(url);
  }

  useNonMobileEffect(() => {
    aoiLayer.clearAois();
  }, []);

  useEffect(() => {
    setIsEventListLoading(true);
    DiseaseActivityApi.DiseaseActivityController_GetDiseaseActivities({
      activityStartDate: appState.startDate,
    })
      .then(data => {
        setEvents(data);
      })
      .finally(() => {
        setIsEventListLoading(false);
      });
  }, [appState.startDate]);

  useEffect(() => {
    if (activePanel === 'ParametersPanel') {
      setIsMinimizedEventListPanel(true);
    } else if (activePanel === 'HistoryPanel') {
      setIsMinimizedEventListPanel(true);
    } else {
      setIsMinimizedEventListPanel(false);
    }
  }, [activePanel]);

  useEffect(() => {
    amendState({ locationFullName: undefined });
  }, []);

  // TODO: 8e0fe250: dup code
  useEffect(() => {
    !isVisibleEDP && amendState({ eventTitleFallback: null });
  }, [isVisibleEDP]);

  useEffect(() => {
    // DESIGN: PT-1191: when URL ids are not in preceeding panel, go to default dashboard
    const isInvalidEvent =
      events &&
      countryId &&
      !(events.eventsList || []).find(
        d =>
          d.eventInformation.diseaseActivityKey.countryId === countryId &&
          d.eventInformation.diseaseActivityKey.diseaseId === diseaseId &&
          d.eventInformation.diseaseActivityKey.speciesId === speciesId
      );
    if (isInvalidEvent)
      kickOutWithNotification(`/all`, `Selected activity is not present in the current list`);
  }, [events, diseaseId, speciesId, countryId]);

  const uiMapLayer = useMemo<'events' | 'details'>(
    () =>
      activePanel === 'EventListPanel' && events
        ? 'events'
        : activePanel === 'HistoryPanel' ||
          ((activePanel === 'EventDetailPanel' || activePanel === 'ParametersPanel') &&
            selectedEventDetails)
        ? 'details'
        : null,
    [activePanel, events, selectedEventDetails]
  );

  useNonMobileEffect(() => {
    if (uiMapLayer === 'events') {
      mapEventsView.updateEventView(events.countryPins, appState.startDate);
      mapEventsView.show();
    } else {
      mapEventsView.hide();
    }
  }, [uiMapLayer, events]);

  useNonMobileEffect(() => {
    if (uiMapLayer === 'details') {
      if (selectedEventDetails) {
        mapEventDetailView.showOutbreakLocations(selectedEventDetails.eventLocations);
        mapEventDetailView.showAirportPins(selectedEventDetails.airports);
      }
    } else {
      mapEventDetailView.hide();
    }
  }, [uiMapLayer, selectedEventDetails]);

  const handleOnEventSelected = useCallback((event: dto.GetEventModel) => {
    const {
      eventInformation: { diseaseActivityKey, title },
    } = event;
    amendState({ eventTitleFallback: title });
    const newDiseaseId = diseaseActivityKey.diseaseId;
    const newSpeciesId = diseaseActivityKey.speciesId;
    const newCountry = diseaseActivityKey.countryId;
    navigateTo(`/all/disease/${newDiseaseId}/species/${newSpeciesId}/country/${newCountry}`);
    notifyEvent({
      action: constants.Action.OPEN_EVENT_DETAILS,
      category: constants.Category.EVENTS,
      label: `Open from list: disease/${newDiseaseId}/species/${newSpeciesId}/country/${newCountry} | ${title}`,
      value: `${newDiseaseId}-${newSpeciesId}-${newCountry}`,
    });
  }, []);

  const handleRiskParametersOnSelect = useCallback(() => {
    navigateTo(`/all/disease/${diseaseId}/species/${speciesId}/country/${countryId}/parameters`);
  }, [countryId, diseaseId, speciesId]);

  const gotoHistory = useCallback(
    (activityDateNew?: string) => {
      const targetURL = activityDateNew
        ? `/all/disease/${diseaseId}/species/${speciesId}/country/${countryId}/activity/${activityDateNew}`
        : `/all/disease/${diseaseId}/species/${speciesId}/country/${countryId}/activity`;
      navigateTo(targetURL);
    },
    [diseaseId, speciesId, countryId]
  );

  const handleOnCloseTransparOrHistory = useCallback(() => {
    navigateTo(`/all/disease/${diseaseId}/species/${speciesId}/country/${countryId}`);
  }, [countryId, diseaseId, speciesId]);

  const handleOnClose = useCallback(() => {
    navigateTo(`/all`);
  }, []);

  const handleOnEventDetailsLoad = useCallback(
    (event: dto.GetEventModel) => {
      setSelectedEventDetails(event);
    },
    [setSelectedEventDetails]
  );

  const handleOnEventDetails404 = useCallback(() => {
    navigateTo(`/all`);
  }, []);

  useEffect(() => {
    if (hasParameters && selectedRiskType && selectedEventDetails) {
      const selectedRisk = GetSelectedRisk(selectedEventDetails, selectedRiskType);
      if (selectedRisk && (selectedRisk.isModelNotRun || selectedRisk.isModelRunning)) {
        // NOTE: 4c87a49b: this means URL is invalid. The parameters panel was opened via URL for "Not Calculated" case
        navigateTo('/all');
      }
    }
  }, [hasParameters, selectedRiskType, selectedEventDetails]);

  return (
    <div
      sx={{
        display: 'flex',
        height: '100%',
        width: ['100%', 'initial'],
        maxWidth: ['100%', 'calc(100vw - 200px)'],
      }}
    >
      <EventListPanel
        activePanel={activePanel}
        disactIds={disactIds}
        events={events}
        onEventSelected={handleOnEventSelected}
        isMinimized={isMinimizedEventListPanel}
        onMinimize={setIsMinimizedEventListPanel}
        isEventListLoading={isEventListLoading}
      />
      {isVisibleEDP && (
        <EventDetailPanel
          activePanel={activePanel}
          disactIds={disactIds}
          onEventDetailsLoad={handleOnEventDetailsLoad}
          onEventDetailsNotFound={handleOnEventDetails404}
          onRiskParametersClicked={(!DisableTRANSPAR && handleRiskParametersOnSelect) || null}
          isRiskParametersSelected={!DisableTRANSPAR && hasParameters}
          onGoToActivityHistory={activityDateNew => gotoHistory(activityDateNew)}
          onSelectedRiskTypeChanged={setSelectedRiskType}
          onClose={handleOnClose}
          isMinimized={isMinimizedEventDetailPanel}
          onMinimize={setIsMinimizedEventDetailPanel}
          seedRiskDirectionType="exportation"
        />
      )}
      {!DisableTRANSPAR && isVisibleTRANSPAR && (
        <TransparencyPanel
          key={`TRANSPAR-${diseaseId}-${speciesId}-${countryId}`}
          event={selectedEventDetails}
          riskType={selectedRiskType}
          onClose={handleOnCloseTransparOrHistory}
          disactIds={disactIds}
          geonameId={null}
          isMinimized={isMinimizedParametersPanel}
          onMinimize={setIsMinimizedParametersPanel}
        />
      )}
      {hasActivity && (
        <DiseaseActivityHistoryPanel
          key={`ActivityHistory-${diseaseId}-${speciesId}-${countryId}`}
          activityAnchorDate={activityDate}
          activePanel={activePanel}
          disactIds={disactIds}
          event={selectedEventDetails}
          onClose={handleOnCloseTransparOrHistory}
          isMinimized={isMinimizedHistoryPanel}
          onMinimize={setIsMinimizedHistoryPanel}
        />
      )}
    </div>
  );
};

export default EventView;
